import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, FolderOpen } from 'lucide-react';
import { getApiErrorMessage } from '@/utils/ValidationErrors';
import portalService from '@/services/portal/portalService';
import { usePortal } from '@/portal/PortalProvider';
import { statusLabel } from './matterStatus';

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;

/** My Matters: every matter at this firm with documents shared with the guest (OL-195). */
const PortalMatters = () => {
  const { guest, branding } = usePortal();
  const [matters, setMatters] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    portalService
      .getMatters()
      .then(setMatters)
      .catch((err) => {
        // A 401 is handled by the provider, which signs the guest out.
        if (err?.response?.status !== 401) setError(getApiErrorMessage(err, 'Your matters could not be loaded.'));
      });
  }, []);

  if (error) {
    return <div className="card"><div className="card-body text-center text-2sm text-danger py-10">{error}</div></div>;
  }

  if (matters === null) {
    return <p className="text-center text-2sm text-gray-600 py-10">Loading…</p>;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-gray-900">Welcome, {guest?.name}</h1>
        <p className="text-2sm text-gray-600">
          These are the matters {branding.firm_name || 'the firm'} has shared documents from.
        </p>
      </div>

      {matters.length === 0 ? (
        <div className="card">
          <div className="card-body flex flex-col items-center gap-3 py-14 text-center">
            <FolderOpen size={32} className="text-gray-400" />
            <h3 className="text-base font-medium text-gray-900">No documents are shared with you right now</h3>
            <p className="text-2sm text-gray-600">When the firm shares documents with you, the matters they belong to appear here.</p>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {matters.map((matter) => (
            <Link key={matter.id} to={`/portal/matters/${matter.id}`} className="card hover:shadow-md transition-shadow">
              <div className="card-body flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex flex-col min-w-0">
                    <span className="text-base font-semibold text-gray-900 truncate">{matter.name}</span>
                    {matter.number && <span className="text-2xs text-gray-500">No. {matter.number}</span>}
                  </div>
                  <span className="badge badge-sm badge-outline shrink-0">{statusLabel(matter.status)}</span>
                </div>
                <div className="flex items-center justify-between text-2sm text-gray-600">
                  <span>
                    {matter.files} {matter.files === 1 ? 'document' : 'documents'}
                    {matter.last_shared_at && <> · updated {fmtDate(matter.last_shared_at)}</>}
                  </span>
                  <ChevronRight size={16} className="text-gray-400" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export { PortalMatters };
